import { FC, useCallback, useState } from "react";
import { FileRejection, useDropzone } from "react-dropzone";
import { styled, Button } from "@monolith-forensics/monolith-ui";
import { Upload, FileSpreadsheet } from "lucide-react";
import { ImportDefinition } from "../DataImport.types";

interface FileUploadStepProps {
  definition: ImportDefinition;
  onFileParsed: (
    columnNames: string[],
    rows: Record<string, string>[],
    file: File | null
  ) => void;
  parsedFileName?: string | null;
  parsedRowCount?: number;
  parsedColumnCount?: number;
}

interface CsvParseError {
  code?: string;
  message: string;
  row?: number;
}

const FileUploadStep: FC<FileUploadStepProps> = ({
  definition,
  onFileParsed,
  parsedFileName,
  parsedRowCount,
  parsedColumnCount,
}) => {
  const maxSize = definition.maxFileSize || 10485760; // 10 MB default
  const [fileError, setFileError] = useState<string | null>(null);

  const formatCsvParseError = useCallback((error: CsvParseError) => {
    const rowText =
      typeof error.row === "number" ? ` on row ${error.row + 1}` : "";

    switch (error.code) {
      case "MissingQuotes":
        return `This CSV appears to have an unmatched quote${rowText}.`;
      case "UndetectableDelimiter":
        return "This CSV could not be parsed because its delimiter could not be detected.";
      case "TooFewFields":
      case "TooManyFields":
        return `This CSV has an inconsistent number of columns${rowText}.`;
      default:
        return `Unable to parse this CSV${rowText}: ${error.message}`;
    }
  }, []);

  const handleDropRejected = useCallback(
    (rejections: FileRejection[]) => {
      const rejection = rejections[0];
      if (!rejection) return;
      const code = rejection.errors[0]?.code;
      if (code === "file-too-large") {
        setFileError(
          `File exceeds the ${Math.round(maxSize / 1024 / 1024)} MB size limit.`
        );
      } else if (code === "file-invalid-type") {
        setFileError("Invalid file type. Please upload a CSV file.");
      } else {
        setFileError(rejection.errors[0]?.message || "File was rejected.");
      }
    },
    [maxSize]
  );

  const handleDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;
      setFileError(null);

      try {
        const PapaMod = await import("papaparse");
        const Papa = PapaMod.default || PapaMod;
        const file = acceptedFiles[0];

        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            if (results.errors.length > 0) {
              setFileError(formatCsvParseError(results.errors[0]));
              onFileParsed([], [], null);
              return;
            }

            const columnNames = results.meta.fields || [];
            if (columnNames.length === 0) {
              setFileError(
                "This CSV is missing a header row, so we could not read any columns."
              );
              onFileParsed([], [], null);
              return;
            }

            const rowData = results.data as Record<string, string>[];
            onFileParsed(columnNames, rowData, file);
          },
          error: (error) => {
            setFileError(`Failed to read CSV file: ${error.message}`);
            onFileParsed([], [], null);
          },
        });
      } catch (error) {
        setFileError(
          error instanceof Error
            ? `Failed to load CSV parser: ${error.message}`
            : "Failed to load CSV parser."
        );
        onFileParsed([], [], null);
      }
    },
    [formatCsvParseError, onFileParsed]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    onDropRejected: handleDropRejected,
    accept: { "text/csv": [".csv"] },
    maxFiles: 1,
    maxSize,
    multiple: false,
  });

  return (
    <Container>
      {definition.description && (
        <Description>{definition.description}</Description>
      )}

      <DropZone {...getRootProps()} $isDragActive={isDragActive}>
        <input {...getInputProps()} />
        <Upload size={40} strokeWidth={1.5} />
        <DropZoneTitle>Upload CSV File</DropZoneTitle>
        <DropZoneSubtext>
          Drag and drop or click to select a CSV file
        </DropZoneSubtext>
        <DropZoneHint>
          Max file size: {Math.round(maxSize / 1024 / 1024)} MB
        </DropZoneHint>
      </DropZone>

      {fileError && <FileError>{fileError}</FileError>}

      {parsedFileName && (
        <FileInfo>
          <FileSpreadsheet size={20} />
          <FileDetails>
            <FileName>{parsedFileName}</FileName>
            <FileMeta>
              {parsedRowCount} rows, {parsedColumnCount} columns
            </FileMeta>
          </FileDetails>
          <Button
            size="xs"
            variant="subtle"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onFileParsed([], [], null);
            }}
          >
            Remove
          </Button>
        </FileInfo>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Description = styled.div`
  font-size: 14px;
  color: ${(props) => props.theme.palette.text.secondary};
  line-height: 1.5;
`;

const DropZone = styled.div<{ $isDragActive: boolean }>`
  border: 2px dashed
    ${(props) =>
      props.$isDragActive
        ? props.theme.palette.primary.main
        : props.theme.palette.divider};
  border-radius: 8px;
  padding: 40px 24px;
  text-align: center;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  color: ${(props) =>
    props.$isDragActive
      ? props.theme.palette.primary.main
      : props.theme.palette.text.secondary};

  &:hover {
    border-color: ${(props) => props.theme.palette.primary.main};
    color: ${(props) => props.theme.palette.primary.main};
  }
`;

const DropZoneTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  margin-top: 4px;
`;

const DropZoneSubtext = styled.div`
  font-size: 14px;
  opacity: 0.8;
`;

const DropZoneHint = styled.div`
  font-size: 12px;
  opacity: 0.6;
  margin-top: 4px;
`;

const FileError = styled.div`
  color: ${(props) => props.theme.palette.error.main};
  font-size: 13px;
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: 1px solid ${(props) => props.theme.palette.divider};
  border-radius: 8px;
  background: ${(props) => props.theme.palette.background.default};
`;

const FileDetails = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const FileName = styled.div`
  font-size: 14px;
  font-weight: 500;
`;

const FileMeta = styled.div`
  font-size: 12px;
  color: ${(props) => props.theme.palette.text.secondary};
`;

export default FileUploadStep;
